// What the user menu shows about the signed-in person. Built only from claims
// that a native (email and password) ID token and a company SSO ID token both
// carry, plus GET /me, so the menu is the same whichever way the person signed
// in. Display only: the API enforces every rule itself. Nothing here returns a
// token, a subject id or a tenant id.
import { TENANT_ROLE_LABELS, ADMIN_ROLE, DEVELOPER_ROLE, QLIK_CREDENTIALS_ROLE, AUDITOR_ROLE, VIEWER_ROLE } from '../../../shared/solution.mjs';

const text = (v) => (typeof v === 'string' ? v.trim() : '');

/** name, else given plus family name, else the email local part. */
export function displayName(claims, fallbackEmail) {
  const name = text(claims?.name);
  if (name) return name;
  const joined = [text(claims?.given_name), text(claims?.family_name)].filter(Boolean).join(' ');
  if (joined) return joined;
  const email = text(claims?.email) || text(fallbackEmail);
  return email ? email.split('@')[0] : '';
}

// Providers in the `identities` claim, whatever shape it arrives in (array, JSON string, or Go-style map string).
function hasFederatedIdentity(raw) {
  if (raw === undefined || raw === null || raw === '') return false;
  if (Array.isArray(raw)) return raw.length > 0;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.length > 0 : Boolean(parsed);
    } catch {
      return /providerName/.test(raw);
    }
  }
  return typeof raw === 'object';
}

export function signInMethod(claims) {
  return hasFederatedIdentity(claims?.identities) ? 'Company SSO' : 'Email and password';
}

const formatDate = (iso) => new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' });

/** Friendly role labels for the active tenant, plus the operator label for staff. */
export function roleLabels(access, isOperator) {
  const a = access ?? {};
  const labels = [];
  if (a.admin === true) labels.push(TENANT_ROLE_LABELS[ADMIN_ROLE]);
  if (a.developer === true) labels.push(TENANT_ROLE_LABELS[DEVELOPER_ROLE]);
  if (a.qlikCredentials === true) labels.push(TENANT_ROLE_LABELS[QLIK_CREDENTIALS_ROLE]);
  if (a.auditor === true) {
    const until = typeof a.auditorExpiresAt === 'string' && !Number.isNaN(Date.parse(a.auditorExpiresAt)) ? a.auditorExpiresAt : null;
    labels.push(until ? `${TENANT_ROLE_LABELS[AUDITOR_ROLE]} (until ${formatDate(until)})` : TENANT_ROLE_LABELS[AUDITOR_ROLE]);
  }
  if (a.viewer === true) labels.push(TENANT_ROLE_LABELS[VIEWER_ROLE]);
  if (isOperator === true) labels.push('IPC Global operator');
  return labels;
}

/**
 * @param {object} p
 * @param {object|null} p.claims - the decoded ID token claims
 * @param {object|null} p.me - the GET /me body
 * @param {string|null} [p.tenantName] - the company name of the viewed tenant when /me has none (View as client)
 */
export function buildUserProfile({ claims, me, tenantName = null }) {
  const email = text(claims?.email) || text(me?.user?.email);
  const authTime = Number(claims?.auth_time);
  return {
    name: displayName(claims, email),
    email,
    company: text(me?.customer?.companyName) || text(tenantName) || null,
    roles: roleLabels(me?.access, me?.user?.isOperator === true),
    signIn: signInMethod(claims),
    lastSignInAt: Number.isFinite(authTime) && authTime > 0 ? new Date(authTime * 1000).toISOString() : null,
  };
}
